import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import {
  ArrowLeft,
  ArrowRight,
  Heart,
  MapPin,
  Users,
  Sparkles,
  Building,
  Mic,
  Clipboard,
  Globe,
  Clock,
  Shield,
  Star,
  Phone,
  Sun,
  Briefcase,
  Camera,
  Award,
  BookOpen,
  Target,
  Car,
  Home,
  Plane,
  Check,
  ChevronRight,
  Calculator,
} from 'lucide-react';
import ItemImageCarousel from '../components/ItemImageCarousel';
import QuoteSimulator from '../components/QuoteSimulator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  heart: Heart,
  'map-pin': MapPin,
  users: Users,
  sparkles: Sparkles,
  building: Building,
  mic: Mic,
  clipboard: Clipboard,
  globe: Globe,
  clock: Clock,
  shield: Shield,
  star: Star,
  phone: Phone,
  sun: Sun,
  briefcase: Briefcase,
  camera: Camera,
  award: Award,
  'book-open': BookOpen,
  target: Target,
  car: Car,
  home: Home,
  plane: Plane,
};

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { language, dir, getLocalizedField } = useLanguage();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);

  useEffect(() => {
    fetchService();
  }, [serviceId]);

  const fetchService = async () => {
    try {
      const response = await axios.get(`${API}/services/${serviceId}`);
      setService(response.data);
    } catch (error) {
      console.error('Error fetching service:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Star;
    return <IconComponent className="w-6 h-6" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!service) return null;

  const longDescription = service[`long_description_${language}`] || service.long_description_fr || '';
  const features = service.features || [];
  const packages = service.packages || [];
  const galleryImages = service.gallery_images || [];

  return (
    <div className="min-h-screen bg-background-light" dir={dir}>
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px]">
        <img
          src={service.image_url}
          alt={getLocalizedField(service, 'title')}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-background-dark/50 to-transparent" />
        
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-6xl mx-auto px-6 pb-16 w-full">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 font-body text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {language === 'ar' ? 'العودة' : 'Retour'}
            </Link>
            
            <p className="overline text-primary mb-3">{service.category.toUpperCase()}</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-white mb-4">
              {getLocalizedField(service, 'title')}
            </h1>
            <p className="font-body text-lg text-white/80 max-w-2xl mb-6">
              {getLocalizedField(service, 'description')}
            </p>
            
            <div className="flex flex-wrap items-center gap-4">
              <span className="font-heading text-2xl text-primary">
                {language === 'ar' ? 'من' : 'À partir de'} {formatPrice(service.base_price)} XAF
              </span>
              <Link
                to={`/booking?service=${service.id}`}
                className="btn-primary flex items-center gap-2"
                data-testid="service-book-btn"
              >
                {language === 'ar' ? 'احجز الآن' : 'Réserver maintenant'}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <div className="sticky top-20 z-30 bg-white border-b border-primary/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex gap-8 overflow-x-auto">
            {['overview', 'packages', ...(service.has_sub_services ? ['catalog', 'simulator'] : []), 'gallery'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 font-body text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-muted hover:text-text-primary'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab === 'simulator' && <Calculator className="w-4 h-4" />}
                {tab === 'overview' ? (language === 'ar' ? 'نظرة عامة' : 'Vue d\'ensemble') :
                 tab === 'packages' ? (language === 'ar' ? 'الباقات' : 'Formules') :
                 tab === 'catalog' ? (language === 'ar' ? 'الكتالوج' : 'Catalogue') :
                 tab === 'simulator' ? (language === 'ar' ? 'محاكي العرض' : 'Devis Instantané') :
                 (language === 'ar' ? 'المعرض' : 'Galerie')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-16 animate-fade-in">
            {/* Long Description */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading text-3xl font-light text-text-primary mb-6">
                  {language === 'ar' ? 'عن هذه الخدمة' : 'À propos de ce service'}
                </h2>
                <div className="prose prose-lg max-w-none">
                  {longDescription.split('\n\n').map((paragraph, idx) => {
                    if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                      return (
                        <h3 key={idx} className="font-heading text-xl font-medium text-text-primary mt-8 mb-4">
                          {paragraph.replace(/\*\*/g, '')}
                        </h3>
                      );
                    }
                    if (paragraph.startsWith('- ')) {
                      return (
                        <ul key={idx} className="space-y-2 my-4">
                          {paragraph.split('\n').map((item, i) => (
                            <li key={i} className="flex items-start gap-2 font-body text-text-muted">
                              <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                              <span>{item.replace('- ', '')}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    return (
                      <p key={idx} className="font-body text-text-muted leading-relaxed mb-4">
                        {paragraph.replace(/\*\*/g, '')}
                      </p>
                    );
                  })}
                </div>
              </div>

              {/* Quick Contact */}
              <div className="lg:col-span-1">
                <div className="bg-surface-light p-6 border border-primary/10 sticky top-40">
                  <h3 className="font-heading text-xl font-medium text-text-primary mb-4">
                    {language === 'ar' ? 'هل لديك أسئلة؟' : 'Des questions ?'}
                  </h3>
                  <p className="font-body text-sm text-text-muted mb-6">
                    {language === 'ar' 
                      ? 'فريقنا متاح للإجابة على جميع استفساراتك.'
                      : 'Notre équipe est disponible pour répondre à toutes vos questions.'}
                  </p>
                  <a
                    href="https://wa.me/237653989061"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary w-full flex items-center justify-center gap-2 mb-3"
                  >
                    <Phone className="w-4 h-4" />
                    WhatsApp
                  </a>
                  <Link
                    to="/#contact"
                    className="btn-outline w-full text-center"
                  >
                    {language === 'ar' ? 'نموذج الاتصال' : 'Formulaire de contact'}
                  </Link>
                </div>
              </div>
            </div>

            {/* Features */}
            {features.length > 0 && (
              <div>
                <h2 className="font-heading text-3xl font-light text-text-primary mb-8 text-center">
                  {language === 'ar' ? 'لماذا تختارنا' : 'Pourquoi nous choisir'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-6 border border-primary/10 hover:shadow-lg transition-shadow"
                    >
                      <div className="w-12 h-12 bg-primary/10 flex items-center justify-center mb-4 text-primary">
                        {getIcon(feature.icon)}
                      </div>
                      <h3 className="font-heading text-lg font-medium text-text-primary mb-2">
                        {feature[`title_${language}`] || feature.title_fr}
                      </h3>
                      <p className="font-body text-sm text-text-muted">
                        {feature[`desc_${language}`] || feature.desc_fr}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Packages Tab */}
        {activeTab === 'packages' && packages.length > 0 && (
          <div className="animate-fade-in">
            <h2 className="font-heading text-3xl font-light text-text-primary mb-8 text-center">
              {language === 'ar' ? 'باقاتنا' : 'Nos Formules'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {packages.map((pkg, idx) => (
                <div
                  key={idx}
                  className={`bg-white border p-8 transition-all hover:shadow-xl ${
                    idx === 1 ? 'border-primary shadow-lg scale-105' : 'border-primary/10'
                  }`}
                >
                  {idx === 1 && (
                    <span className="inline-block bg-primary text-white text-xs font-body px-3 py-1 mb-4">
                      {language === 'ar' ? 'الأكثر شعبية' : 'PLUS POPULAIRE'}
                    </span>
                  )}
                  <h3 className="font-heading text-2xl font-medium text-text-primary mb-2">
                    {pkg[`name_${language}`] || pkg.name_fr}
                  </h3>
                  <p className="font-body text-sm text-text-muted mb-4">
                    {pkg.guests && `${pkg.guests} ${language === 'ar' ? 'ضيف' : 'invités'}`}
                    {pkg.duration && pkg.duration}
                  </p>
                  <p className="font-heading text-3xl text-primary mb-6">
                    {formatPrice(pkg.price)} <span className="text-lg">XAF</span>
                  </p>
                  <ul className="space-y-3 mb-8">
                    {(pkg[`includes_${language}`] || pkg.includes_fr || []).map((item, i) => (
                      <li key={i} className="flex items-start gap-2 font-body text-sm text-text-muted">
                        <Check className="w-5 h-5 text-primary flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/booking?service=${service.id}`}
                    className={`w-full text-center block ${idx === 1 ? 'btn-primary' : 'btn-outline'}`}
                  >
                    {language === 'ar' ? 'اختر هذه الباقة' : 'Choisir cette formule'}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Catalog Tab (for Concierge) */}
        {activeTab === 'catalog' && service.has_sub_services && service.sub_services && (
          <div className="space-y-16 animate-fade-in">
            {service.sub_services.map((subService, idx) => (
              <div key={idx} className="border-b border-primary/10 pb-16 last:border-0">
                <h2 className="font-heading text-3xl font-light text-text-primary mb-4">
                  {getLocalizedField(subService, 'title')}
                </h2>
                <p className="font-body text-text-muted mb-8 max-w-3xl">
                  {getLocalizedField(subService, 'description')}
                </p>

                {/* Vehicles Catalog */}
                {subService.type === 'vehicle_rental' && subService.vehicles && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {subService.vehicles.map((vehicle, vidx) => (
                      <div
                        key={vidx}
                        className="bg-white border border-primary/10 overflow-hidden hover:shadow-lg transition-all"
                      >
                        <ItemImageCarousel
                          images={vehicle.images && vehicle.images.length > 0 ? vehicle.images : (vehicle.image ? [vehicle.image] : [])}
                          alt={vehicle.name}
                        />
                        <div className="p-5">
                          <div className="flex items-start justify-between mb-3">
                            <h3 className="font-heading text-lg font-medium text-text-primary">
                              {vehicle.name}
                            </h3>
                            <span className="font-body text-xs text-text-muted bg-surface-light px-2 py-1">
                              {vehicle.passengers} {language === 'ar' ? 'ركاب' : 'places'}
                            </span>
                          </div>
                          <ul className="space-y-1 mb-4">
                            {(vehicle[`features_${language}`] || vehicle.features_fr || []).slice(0, 3).map((f, i) => (
                              <li key={i} className="font-body text-xs text-text-muted flex items-center gap-1">
                                <Check className="w-3 h-3 text-primary" />
                                {f}
                              </li>
                            ))}
                          </ul>
                          <div className="flex items-center justify-between">
                            <p className="font-heading text-xl text-primary">
                              {formatPrice(vehicle.price)} <span className="text-sm">XAF/jour</span>
                            </p>
                            <Link
                              to={`/booking?service=${service.id}`}
                              className="text-sm font-body text-primary hover:text-secondary flex items-center gap-1"
                            >
                              Réserver <ChevronRight className="w-4 h-4" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Venues Catalog */}
                {subService.type === 'venue_rental' && subService.venues && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {subService.venues.map((venue, vidx) => (
                      <div
                        key={vidx}
                        className="bg-white border border-primary/10 overflow-hidden hover:shadow-lg transition-shadow"
                      >
                        <ItemImageCarousel
                          images={venue.images || []}
                          alt={venue.name_fr}
                          aspectClass="aspect-[16/9]"
                        />
                        <div className="p-6">
                          <span className="inline-block bg-secondary/10 text-secondary text-xs font-body px-2 py-1 mb-3">
                            {venue.type.toUpperCase()}
                          </span>
                          <h3 className="font-heading text-xl font-medium text-text-primary mb-2">
                            {venue[`name_${language}`] || venue.name_fr}
                          </h3>
                          <p className="font-body text-sm text-text-muted mb-3">
                            {language === 'ar' ? 'السعة' : 'Capacité'}: {venue.capacity} {language === 'ar' ? 'شخص' : 'personnes'}
                          </p>
                          <ul className="space-y-1 mb-4">
                            {(venue[`features_${language}`] || venue.features_fr || []).map((f, i) => (
                              <li key={i} className="font-body text-xs text-text-muted flex items-center gap-1">
                                <Check className="w-3 h-3 text-primary" />
                                {f}
                              </li>
                            ))}
                          </ul>
                          <div className="flex items-center justify-between pt-4 border-t border-primary/10">
                            <p className="font-heading text-xl text-primary">
                              {formatPrice(venue.price)} <span className="text-sm">XAF/jour</span>
                            </p>
                            <Link
                              to={`/booking?service=${service.id}`}
                              className="btn-outline text-sm py-2"
                            >
                              Réserver
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Accommodations Catalog */}
                {subService.type === 'accommodation' && subService.accommodations && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {subService.accommodations.map((acc, aidx) => (
                      <div
                        key={aidx}
                        className="bg-white border border-primary/10 overflow-hidden hover:shadow-lg transition-shadow"
                      >
                        <ItemImageCarousel images={acc.images || []} alt={acc.name_fr} />
                        <div className="p-5">
                          <span className="inline-block bg-primary/10 text-primary text-xs font-body px-2 py-1 mb-3">
                            {acc.type.toUpperCase().replace('_', ' ')}
                          </span>
                          <h3 className="font-heading text-lg font-medium text-text-primary mb-2">
                            {acc[`name_${language}`] || acc.name_fr}
                          </h3>
                          <p className="font-body text-sm text-text-muted mb-3">
                            {acc.capacity} {language === 'ar' ? 'شخص' : 'personnes'}
                          </p>
                          <ul className="space-y-1 mb-4">
                            {(acc[`features_${language}`] || acc.features_fr || []).map((f, i) => (
                              <li key={i} className="font-body text-xs text-text-muted flex items-center gap-1">
                                <Check className="w-3 h-3 text-primary" />
                                {f}
                              </li>
                            ))}
                          </ul>
                          <p className="font-heading text-xl text-primary">
                            {formatPrice(acc.price)} <span className="text-sm">XAF/nuit</span>
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Airport VIP Services */}
                {subService.type === 'airport_vip' && subService.services && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {subService.services.map((svc, sidx) => (
                      <div
                        key={sidx}
                        className={`bg-white border p-6 transition-all ${
                          sidx === 1 ? 'border-primary shadow-lg' : 'border-primary/10 hover:shadow-lg'
                        }`}
                      >
                        <h3 className="font-heading text-xl font-medium text-text-primary mb-4">
                          {svc[`name_${language}`] || svc.name_fr}
                        </h3>
                        <p className="font-heading text-2xl text-primary mb-4">
                          {formatPrice(svc.price)} <span className="text-sm">XAF</span>
                        </p>
                        <ul className="space-y-2">
                          {(svc[`includes_${language}`] || svc.includes_fr || []).map((item, i) => (
                            <li key={i} className="font-body text-sm text-text-muted flex items-start gap-2">
                              <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Simulator Tab */}
        {activeTab === 'simulator' && service.has_sub_services && (
          <div className="animate-fade-in">
            <QuoteSimulator
              service={service}
              language={language}
              dir={dir}
              getLocalizedField={getLocalizedField}
            />
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && galleryImages.length > 0 && (
          <div className="animate-fade-in">
            <h2 className="font-heading text-3xl font-light text-text-primary mb-8 text-center">
              {language === 'ar' ? 'معرض الصور' : 'Galerie Photos'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {galleryImages.map((img, idx) => (
                <div
                  key={idx}
                  className={`overflow-hidden ${idx === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                >
                  <img
                    src={img}
                    alt={`Gallery ${idx + 1}`}
                    className={`w-full object-cover hover:scale-105 transition-transform duration-500 ${
                      idx === 0 ? 'h-[400px] md:h-full' : 'h-[250px]'
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <section className="bg-background-dark py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl font-light text-white mb-4">
            {language === 'ar' ? 'جاهز لبدء مشروعك؟' : 'Prêt à concrétiser votre projet ?'}
          </h2>
          <p className="font-body text-white/70 mb-8">
            {language === 'ar'
              ? 'فريقنا جاهز لتحويل رؤيتك إلى واقع.'
              : 'Notre équipe est prête à transformer votre vision en réalité.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={`/booking?service=${service.id}`}
              className="btn-primary flex items-center gap-2"
            >
              {language === 'ar' ? 'احجز الآن' : 'Réserver maintenant'}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/#contact"
              className="btn-gold"
            >
              {language === 'ar' ? 'طلب عرض أسعار' : 'Demander un devis'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
